/* eslint-disable max-len */
import React, { useState } from 'react';
import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { useSubmitter } from '../../platform/Hook';
import { ResultInfoForSubmitter } from '../../ui/ResultInfo';
import { useResolve } from '../Container';
import { Copy, Em, Flexer, Title } from '../dashboard/elements/LayoutElements';
import { isItXmas } from '../occasions/xmas/WithXmas';
import { HandwritingModeOption, MouseModeOption, PreferencesModel } from '../preferences/PreferencesModel';
import { Grid, Select, Toggle } from './../../ui';
import { OptionControl, OptionInfo } from './PreferencesElements';

export const ControlsView = () => {

    const api = useResolve(ApiClient);
    const preferences = useResolve(PreferencesModel);
    const [state, setState] = useState(preferences.getState());

    const [onSubmit, _working, result] = useSubmitter(async (patch: Partial<typeof state>) => {
        const before = Object.fromEntries(Object.keys(patch).map(x => [x, state[x]]));
        setState(x => ({ ...x, ...patch }));
        try {
            await api.exec(v2.me.preferences.update(patch));
        }
        catch (e) {
            setState(x => ({ ...x, ...before }));
            throw e;
        }
    }, [state]);

    return <Flexer width="42">
        <Grid columns="auto 10rem" columnGap="1rem" rowGap="2rem">
            <OptionInfo>
                <Title>Handwriting Mode</Title>
                <Copy>
                    Controls the personalized handwriting feature.
                    Choose <Em>Uniform</Em> for a single easy to read handwriting font or <Em>Off</Em> for a standard sans-serif font.
                </Copy>
            </OptionInfo>
            <OptionControl>
                <Select
                    items={HandwritingModeOption.all}
                    selected={HandwritingModeOption.for(state.handwritingMode)}
                    onChange={x => onSubmit({ handwritingMode: x.key })} />
            </OptionControl>
            <OptionInfo>
                <Title>Mouse Mode</Title>
                <Copy>
                    Controls how the application detects your mouse type.
                    This affects how the zooming works. <Em>Auto</Em> will try and guess,
                    if you are having issues set this manually to the device you are using.
                </Copy>
            </OptionInfo>
            <OptionControl>
                <Select
                    items={MouseModeOption.all}
                    selected={MouseModeOption.for(state.mouseMode)}
                    onChange={x => onSubmit({ mouseMode: x.key })} />
            </OptionControl>
            <OptionInfo>
                <Title>Allow Slapping</Title>
                <Copy>Turning this off prevents people from being able to virtually slap you.
                    Also prevents you from slapping others, because that would be unfair.</Copy>
            </OptionInfo>
            <OptionControl>
                <Toggle value={state.slapping} onAction={x => onSubmit({ slapping: x })} />
            </OptionControl>
            <OptionInfo>
                <Title>Disable Object Shadows</Title>
                <Copy>
                    Disables shadow effects on Stickies, Cards and other board objects.  You should see an improvement in pan and zoom performance with this setting enabled.
                </Copy>
            </OptionInfo>
            <OptionControl>
                <Toggle value={state.gfxLevel < 2} onAction={x => onSubmit({ gfxLevel: x ? 1 : 2 })} />
            </OptionControl>
            <OptionInfo>
                <Title>Hide Other Users</Title>
                <Copy>
                    When enabled, other users will only be visible when they move or edit a sticky note or board object.
                </Copy>
            </OptionInfo>
            <OptionControl>
                <Toggle value={state.hideOthers} onAction={x => onSubmit({ hideOthers: x })} />
            </OptionControl>
            {!!isItXmas() && <>
                <OptionInfo>
                    <Title>Scrooge Mode</Title>
                    <Copy>When enabled, festive decorations will be removed.</Copy>
                </OptionInfo>
                <OptionControl>
                    <Toggle value={state.scrooge2024} onAction={x => onSubmit({ scrooge2024: x })} />
                </OptionControl>
            </>}
        </Grid>
        <Flexer margin="2 0">
            <ResultInfoForSubmitter result={result} onSuccess="Changes saved." />
        </Flexer>
    </Flexer>;
};
